import { useCallback, useEffect, useState } from 'react';
import { timeAndLeaveGateway } from '../index';
import type {
  EscoHrExceptionResponse, EscoHrLiveAttendanceResponse, EscoHrLiveState, EscoHrProcessResult, EscoHrPunchResponse, EscoHrStatusResponse, EscoHrSyntheticResult,
} from '../types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';

const liveStateLabels: Record<EscoHrLiveState, string> = {
  ClockedIn: 'Clocked in',
  OnMeal: 'On meal',
  OnBreak: 'On break',
  ClockedOut: 'Clocked out',
};

const punchTypeLabels: Record<EscoHrPunchResponse['punchType'], string> = {
  ClockIn: 'Clock in',
  ClockOut: 'Clock out',
  MealStart: 'Meal start',
  MealEnd: 'Meal end',
  RestStart: 'Break start',
  RestEnd: 'Break end',
  Transfer: 'Transfer',
};

/** Plain-language versions of the exception codes the server reports. An unknown code is shown generically, never raw. */
const exceptionLabels: Record<string, string> = {
  ClockInWhileOpen: 'Clocked in again before clocking out.',
  ClockOutWithoutClockIn: 'A clock-out with no clock-in before it.',
  MealOpenAtClockOut: 'Clocked out while a meal break was still open.',
  BreakOpenAtClockOut: 'Clocked out while a break was still open.',
  MealEndWithoutStart: 'A meal break ended that was never started.',
  BreakEndWithoutStart: 'A break ended that was never started.',
  MealStartOutsideShift: 'A meal break started outside a shift.',
  BreakStartOutsideShift: 'A break started outside a shift.',
  NestedBreak: 'A break started while another was still open.',
  TransferOutsideShift: 'A transfer outside a shift.',
  MissingAuthoritativeTime: 'The event had no punch time, so no punch was recorded.',
  InvalidTimestamp: 'The event had a punch time that could not be read, so no punch was recorded.',
  MissingPunchId: 'The event had no usable punch identifier, so no punch was recorded.',
  InvalidRecordVersion: 'The event had an unusable record version, so no punch was recorded.',
  UnreadablePayload: 'The event could not be read.',
  WaitingForOriginalPunch: 'A void is waiting for the punch it cancels to arrive.',
  DuplicatePunch: 'A repeat of a punch already recorded; ignored.',
  NotSupportedYet: 'Recorded, but not used yet: this version records punches only.',
  EmployeeNotMapped: 'The escoHR employee is not mapped to an EPN employee yet.',
  AnalysisTruncated: 'Only the most recent punches were analysed.',
  EnvelopeMismatch: 'The event did not match its own headers.',
  UnsupportedSchemaVersion: 'The event used a version EPN does not support.',
  UnsupportedEventType: 'The event type is not one EPN accepts.',
  InvalidOccurredAt: 'The event had no valid time.',
  PersonReferenceMissing: 'The event did not identify an employee.',
  MalformedJson: 'The event could not be read.',
};

function when(value: string | null): string {
  return value ? new Date(value).toLocaleString() : '—';
}

function SyntheticBadge() {
  return <span className="badge badge--caution">Synthetic Demo Data</span>;
}

function processSummary(r: EscoHrProcessResult): string {
  const parts = [`${r.processed} processed`];
  if (r.duplicatesIgnored) parts.push(`${r.duplicatesIgnored} repeat${r.duplicatesIgnored === 1 ? '' : 's'} ignored`);
  if (r.exceptions) parts.push(`${r.exceptions} could not be recorded`);
  if (r.waiting) parts.push(`${r.waiting} waiting`);
  if (r.notSupportedYet) parts.push(`${r.notSupportedYet} not used yet`);
  parts.push(`${r.remaining} still pending`);
  return parts.join(', ') + '.';
}

/**
 * Live attendance, punches and exceptions from escoHR, read-only. These are records of what escoHR reported, shown for testing: a punch
 * never creates hours, earnings, payroll or a pay statement. Live status is only as current as the events EPN has received (an offline
 * punch can arrive late), and nothing is processed in the background: an admin of an entitled company runs it. Rows that came from EPN's
 * own staging simulator are labelled Synthetic Demo Data. Nothing here is a payroll or time-zone rule.
 */
export function EscoHrAttendanceSection({
  status, canManage, entitled, employeeName, onChanged,
}: {
  status: EscoHrStatusResponse;
  canManage: boolean;
  entitled: boolean;
  employeeName: (employeeId: string) => string;
  onChanged: () => void;
}) {
  const [live, setLive] = useState<EscoHrLiveAttendanceResponse[]>([]);
  const [punches, setPunches] = useState<EscoHrPunchResponse[]>([]);
  const [exceptions, setExceptions] = useState<EscoHrExceptionResponse[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const [l, p, x] = await Promise.all([
        timeAndLeaveGateway.getEscoHrLiveAttendance(signal),
        timeAndLeaveGateway.listEscoHrPunches(50, signal),
        timeAndLeaveGateway.listEscoHrExceptions(signal),
      ]);
      if (signal?.aborted) return;
      setLive(l);
      setPunches(p);
      setExceptions(x);
      setError(null);
      setLoaded(true);
    } catch (err) {
      if (signal?.aborted) return;
      setError(friendlyErrorMessage(err, 'Could not load attendance from escoHR.'));
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load(controller.signal);
    return () => controller.abort();
  }, [load, status.pendingProcessingEvents, status.confirmedMappings]);

  async function run(action: () => Promise<string>, fallback: string) {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      setNotice(await action());
      await load();
      onChanged();
    } catch (err) {
      setError(friendlyErrorMessage(err, fallback));
    } finally {
      setBusy(false);
    }
  }

  const process = () => run(async () => processSummary(await timeAndLeaveGateway.processEscoHrEvents()), 'Could not process the received events.');

  const generate = () => run(async () => {
    const r: EscoHrSyntheticResult = await timeAndLeaveGateway.generateEscoHrSyntheticData();
    return `Synthetic Demo Data sent: ${r.accepted} accepted, ${r.duplicatesIgnored} repeat ignored, ${r.mappingRequired} need a mapping. Map SYN-001 to SYN-008 to employees, then process.`;
  }, 'Could not generate synthetic demo data.');

  const anySynthetic = live.some((l) => l.isSynthetic) || punches.some((p) => p.isSynthetic) || exceptions.some((x) => x.isSynthetic);

  return (
    <section data-testid="escohr-attendance" aria-label="escoHR attendance">
      <h4 style={{ marginTop: 16 }}>Live attendance and punches</h4>
      <p className="muted">
        Records of what escoHR reported, for testing. They do not create hours, earnings, payroll or pay statements. Status reflects the events received so far; an offline punch can arrive late.
      </p>
      {anySynthetic && <p data-testid="escohr-synthetic-banner"><SyntheticBadge /> Some of what follows is synthetic and is not real employee time.</p>}
      {error && <p className="error" role="alert">{error}</p>}
      {notice && <p role="status" data-testid="escohr-attendance-notice">{notice}</p>}

      {canManage && entitled && (
        <p>
          <button type="button" disabled={busy} onClick={() => void process()}>
            Process received events{status.pendingProcessingEvents > 0 ? ` (${status.pendingProcessingEvents} pending)` : ''}
          </button>
          {status.syntheticDataAvailable && (
            <>
              {' '}
              <button type="button" disabled={busy} onClick={() => void generate()}>Generate synthetic demo data</button>
              <span className="muted"> Staging only. Sent through the same signed path real events use.</span>
            </>
          )}
        </p>
      )}

      {!loaded && <p className="muted">Loading…</p>}

      <table aria-label="escoHR live attendance">
        <thead><tr><th>Employee</th><th>Status</th><th>Since</th><th>Last punch</th><th></th></tr></thead>
        <tbody>
          {live.map((l) => (
            <tr key={l.epnEmployeeId}>
              <td>{employeeName(l.epnEmployeeId)}</td>
              <td>{liveStateLabels[l.state] ?? l.state}</td>
              <td>{when(l.sinceUtc)}</td>
              <td>{when(l.lastPunchAtUtc)}</td>
              <td>{l.isSynthetic && <SyntheticBadge />}</td>
            </tr>
          ))}
          {loaded && live.length === 0 && <tr><td colSpan={5} className="muted">No punches have been recorded yet.</td></tr>}
        </tbody>
      </table>

      <h4 style={{ marginTop: 16 }}>Recent punches</h4>
      <table aria-label="escoHR punches">
        <thead><tr><th>Punch time</th><th>Employee</th><th>Type</th><th>Reported by the device</th><th></th></tr></thead>
        <tbody>
          {punches.map((p) => (
            <tr key={p.id}>
              <td>{when(p.authoritativeTimestampUtc)}</td>
              <td>{employeeName(p.epnEmployeeId)}</td>
              <td>{punchTypeLabels[p.punchType] ?? p.punchType}{p.isVoided && <div className="muted">Voided</div>}</td>
              <td className="muted">
                {p.deviceTimestampUtc ? when(p.deviceTimestampUtc) : 'Not reported'}
                {p.deviceTimeZone ? `, ${p.deviceTimeZone}` : ''}
                {p.offline && <div>Recorded offline</div>}
              </td>
              <td>{p.isSynthetic && <SyntheticBadge />}</td>
            </tr>
          ))}
          {loaded && punches.length === 0 && <tr><td colSpan={5} className="muted">No punches to show.</td></tr>}
        </tbody>
      </table>

      <h4 style={{ marginTop: 16 }}>Exceptions</h4>
      <ul aria-label="escoHR exceptions">
        {exceptions.map((x, i) => (
          <li key={`${x.source}-${x.code}-${x.atUtc}-${i}`}>
            <strong>{x.severity}</strong>: {exceptionLabels[x.code] ?? 'See the escoHR connection log.'}
            {' '}<span className="muted">
              {x.epnEmployeeId ? employeeName(x.epnEmployeeId) : x.escoHrEmployeeRef ? `escoHR employee ${x.escoHrEmployeeRef}` : ''} {when(x.atUtc)}
            </span>
            {x.isSynthetic && <> <SyntheticBadge /></>}
          </li>
        ))}
        {loaded && exceptions.length === 0 && <li className="muted">No exceptions.</li>}
      </ul>
    </section>
  );
}
